// src/services/kanbanDataService.js

/**
 * Cria uma cópia profunda de forma mais eficiente
 */
function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => deepClone(item));
    }

    const clone = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            clone[key] = deepClone(obj[key]);
        }
    }
    return clone;
}

/**
 * Calcula o total de comentários e mídias para um card
 */
function getCommentsAndMediasCount(item) {
    if (!item?.comments) {
        return { total: 0, medias: 0 };
    }

    const total = item.comments.length;
    let medias = 0;

    for (let i = 0; i < total; i++) {
        medias += item.comments[i].medias?.length || 0;
    }

    return { total, medias };
}

/**
 * Mescla novos cards com os dados existentes de uma coluna
 * @param {Object} existingKanban - Dados atuais do Kanban já formatados
 * @param {string} columnId - ID da coluna no formato "column-123"
 * @param {Object} newData - Dados novos recebidos da API
 * @returns {Object} Kanban atualizado com os novos cards
 */
function mergeKanbanCards(existingKanban, columnId, newData) {

    // Validações
    if (!existingKanban || !columnId || !newData) {
        console.error('mergeKanbanCards - Parâmetros inválidos', { existingKanban, columnId, newData });
        return existingKanban;
    }

    // Clone eficiente
    const updatedKanban = deepClone(existingKanban);

    // Verificar se a coluna existe
    if (!updatedKanban.columns || !updatedKanban.columns[columnId]) {
        console.error(`mergeKanbanCards - Coluna ${columnId} não encontrada`);
        return existingKanban;
    }

    const column = updatedKanban.columns[columnId];

    // Atualizar paginação
    column.pagination = {
        count: newData.count || 0,
        hasMore: !!newData.hasMore,
        page: Number(newData.page || (column.pagination?.page || 1)) + 1
    };

    // Processar novos cards se existirem
    if (Array.isArray(newData.cards) && newData.cards.length > 0) {
        // Criar um Set para evitar duplicatas
        const existingCardIds = new Set(column.cardIds);

        // Adicionar novos cards
        newData.cards.forEach(card => {
            if (!card || !card.id) {
                console.warn('Card inválido encontrado:', card);
                return;
            }

            const cardId = `card-${card.id}`;

            // Apenas adicionar se o card ainda não existir
            if (!existingCardIds.has(cardId)) {
                // Criar objeto do card no formato esperado
                const cardData = {
                    id: cardId,
                    name: card.title || '',
                    description: card.description || ''
                };

                // Adicionar propriedades opcionais de forma mais eficiente
                const optionalProps = {
                    tags: card.tags,
                    amount: card.amount,
                    order: card.order,
                    creator: card.creator,
                    createdAt: card.createdAt,
                    updatedAt: card.updatedAt,
                    kanbanPriorityLevel: card.kanbanPriorityLevel,
                    taskNumber: card.taskNumber,
                    assigned: card.assigned,
                    requester: card.requester,
                    tasks: card.tasks
                };

                Object.entries(optionalProps).forEach(([key, value]) => {
                    if (value !== undefined) {
                        cardData[key] = value;
                    }
                });

                // Processar comentários
                if (card.comments !== undefined) {
                    cardData.comments = getCommentsAndMediasCount(card);
                }

                // Adicionar card ao objeto de cards
                column.cards[cardId] = cardData;

                // Adicionar cardId à lista
                column.cardIds.push(cardId);
                existingCardIds.add(cardId);
            }
        });

        // Reordenar cardIds baseado no campo order
        column.cardIds.sort((a, b) => {
            const orderA = column.cards[a]?.order ?? 9999999;
            const orderB = column.cards[b]?.order ?? 9999999;
            return orderA - orderB;
        });
    }

    return updatedKanban;
}

class KanbanDataService {
    constructor() {
        // Nenhuma inicialização necessária
    }

    /**
     * Mescla novos cards com os dados existentes
     * @param {Object} existingKanban - Dados atuais do Kanban
     * @param {string} columnId - ID da coluna
     * @param {Object} newData - Novos dados da API
     * @returns {Object} Kanban atualizado
     */
    mergeNewCards(existingKanban, columnId, newData) {
        return mergeKanbanCards(existingKanban, columnId, newData);
    }

    /**
     * Transforma dados do Kanban
     * @param {Object} kanbanData - Dados brutos do Kanban
     * @returns {Promise<Object>} Promise com dados transformados
     */
    transformKanbanData(kanbanData) {
        // Validação antecipada
        if (!kanbanData?.kanban?.column || !kanbanData?.columns) {
            console.error('transformKanbanData - Dados inválidos', kanbanData);
            return Promise.resolve(null);
        }

        return new Promise(resolve => {
            try {
                const transformedData = {
                    id: kanbanData?.kanban?.id,
                    taskTitle: kanbanData?.kanban?.taskTitle || '',
                    title: kanbanData?.kanban?.title || '',
                    columns: {},
                    columnOrder: []
                };

                const columnsWithPositions = [];
                const kanbanColumns = kanbanData.kanban.column;

                // Processar colunas e coletar informações de posição
                kanbanColumns.forEach(col => {
                    if (!col || !col.id) {
                        console.warn('Coluna inválida encontrada:', col);
                        return;
                    }

                    const columnId = `column-${col.id}`;
                    const columnData = kanbanData.columns[col.id];
                    // Criar estrutura de coluna
                    const column = {
                        id: columnId,
                        title: col.title || '',
                        cardIds: [],
                        // Use ?? em vez de || para tratar corretamente o 0
                        position: col.position ?? 9999999,
                        cards: {},
                        pagination: {
                            count: columnData?.count || 0,
                            hasMore: !!columnData?.hasMore,
                            page: columnData?.page || 1
                        }
                    };

                    // Adicionar propriedades opcionais da coluna
                    if (col.color !== undefined) column.color = col.color;
                    if (col.hasStart !== undefined) column.hasStart = col.hasStart;
                    if (col.hasFinished !== undefined) column.hasFinished = col.hasFinished;
                    if (col.hasConcluded !== undefined) column.hasConcluded = col.hasConcluded;

                    // Processar cards se existirem
                    if (columnData?.cards && Array.isArray(columnData.cards)) {
                        const cards = [...columnData.cards];

                        // Ordenar cards
                        cards.sort((a, b) => (a.order ?? 9999999) - (b.order ?? 9999999));

                        // Processar cada card
                        cards.forEach(card => {
                            if (!card || !card.id) {
                                console.warn('Card inválido encontrado:', card);
                                return;
                            }

                            const cardId = `card-${card.id}`;

                            // Criar objeto do card
                            const cardData = {
                                id: cardId,
                                name: card.title || '',
                                description: card.description || ''
                            };

                            // Adicionar propriedades opcionais
                            const optionalProps = {
                                tags: card.tags,
                                amount: card.amount,
                                order: card.order,
                                creator: card.creator,
                                createdAt: card.createdAt,
                                updatedAt: card.updatedAt,
                                kanbanPriorityLevel: card.kanbanPriorityLevel,
                                taskNumber: card.taskNumber,
                                assigned: card.assigned,
                                requester: card.requester,
                                tasks: card.tasks
                            };

                            Object.entries(optionalProps).forEach(([key, value]) => {
                                if (value !== undefined) {
                                    cardData[key] = value;
                                }
                            });

                            // Processar comentários
                            if (card.comments !== undefined) {
                                cardData.comments = getCommentsAndMediasCount(card);
                            }

                            column.cards[cardId] = cardData;
                            column.cardIds.push(cardId);
                        });
                    }

                    transformedData.columns[columnId] = column;

                    // Armazenar coluna com sua posição para ordenação posterior
                    columnsWithPositions.push({
                        id: columnId,
                        position: col.position ?? 9999999
                    });
                });

                // Ordenar colunas pela posição
                columnsWithPositions.sort((a, b) => a.position - b.position);

                // Preencher columnOrder com base na ordenação
                transformedData.columnOrder = columnsWithPositions.map(col => col.id);

                resolve(transformedData);
            } catch (error) {
                console.error('transformKanbanData - Erro na transformação:', error);
                resolve(null);
            }
        });
    }

    /**
     * Libera recursos
     */
    dispose() {
        // Não há recursos para liberar
    }
}

// Exportar instância única
const kanbanDataService = new KanbanDataService();
export default kanbanDataService;